import { pdf } from "@react-pdf/renderer";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { IMember } from "@wingspanhq/users";
import { InternalAccount } from "@wingspanhq/wingspan-api-sdk";
import { internalAccountsApi } from "@api";
import { useWSMutation } from "../../../../query/helpers";
import { accountTypeToHumanReadable } from "../../utils/accountTypeToHumanReadable";
import { StatementPDFDocument } from "../StatementPDFDocument/StatementPDFDocument";

/**
 * Custom (non-SDK) composed mutation: fetches the full statement (with entries)
 * for the given internal account via v3, renders the branded PDF client-side,
 * and triggers a browser download. The list endpoint omits `entries`, so the
 * single-statement read is always required to populate the transactions table.
 */
export const useMutationDownloadStatement = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation(
    async ({
      statementId,
      internalAccount,
      member
    }: {
      statementId: string;
      internalAccount: InternalAccount;
      member: IMember;
    }) => {
      // The list endpoint omits `entries`, so always fetch the full single
      // statement fresh to populate the transactions table.
      const statement = await internalAccountsApi.getInternalAccountStatement({
        internalAccountId: internalAccount.id,
        statementId
      });

      try {
        const blob = await pdf(
          <StatementPDFDocument
            statement={statement}
            internalAccount={internalAccount}
            member={member}
          />
        ).toBlob();
        const url = URL.createObjectURL(blob);

        const accountTypeLabel = statement.accountType
          ? accountTypeToHumanReadable(statement.accountType)
          : "Statement";

        const link = document.createElement("a");
        link.href = url;
        const startLabel = statement.startDate.toISOString().slice(0, 10);
        const endLabel = statement.endDate.toISOString().slice(0, 10);
        link.download = `${accountTypeLabel} Statement (${startLabel}-${endLabel}).pdf`;
        document.body.appendChild(link);
        link.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(link);
        openSnackbar({ type: "success", message: "Statement downloaded" });
      } catch (error) {
        openSnackbar({
          type: "error",
          message: "Statement downloading is failed"
        });
      }
    }
  );
};
